import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertCircle, FileText, Upload } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

import BackButton from '../../components/BackButton';
import '../ProcesVerbaux.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _API = `${BACKEND_URL}/api`;
import { useProcesVerbaux } from './useProcesVerbaux';
import ProcesVerbauxDetailDialog from './ProcesVerbauxDetailDialog';

const ProcesVerbaux = () => {
  const ctx = useProcesVerbaux();
  const { filterStatus, filteredPVList, getPenaliteText, getStatusBadge, handleUploadPv, loading, pvList, searchQuery, selectedPv, setDialogOpen, setFilterStatus, setSearchQuery, setSelectedPv, setSortField, setSortOrder, setUploadDialogOpen, setUploadFile, sortField, sortOrder, uploadDialogOpen, uploadFile, uploadLoading } = ctx;
  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Bouton retour */}
      <BackButton label="Retour" className="mb-2 no-print" />
      
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold">Procès-Verbaux de Chantier</h1>
            <p className="text-gray-600">Gérez vos procès-verbaux de réception de travaux</p>
          </div>
        </div>
        
        <Button 
          variant="outline" 
          onClick={() => window.print()}
          className="flex items-center gap-2"
        >
          <FileText className="h-4 w-4" />
          Imprimer / PDF
        </Button>
        <div className="no-print">
        <ProcesVerbauxDetailDialog ctx={ctx} />
        </div>
      </div>

      {pvList.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun procès-verbal enregistré</p>
              <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                Créer votre premier PV
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Barre de filtres et recherche */}
          <Card className="no-print">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Recherche */}
                <div>
                  <Label htmlFor="search">Rechercher</Label>
                  <Input
                    id="search"
                    placeholder="Nom, type..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Filtre par statut */}
                <div>
                  <Label htmlFor="filterStatus">Statut</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="en_temps">Dans les temps</SelectItem>
                      <SelectItem value="retard">En retard</SelectItem>
                      <SelectItem value="gratuite_perdue">Gratuité perdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tri par champ */}
                <div>
                  <Label htmlFor="sortField">Trier par</Label>
                  <Select value={sortField} onValueChange={setSortField}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date_reception">Date réception</SelectItem>
                      <SelectItem value="date_limite_depot">Date limite</SelectItem>
                      <SelectItem value="cout_ht_intervention">Coût (Montant de base)</SelectItem>
                      <SelectItem value="nom_chantier">Nom chantier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Ordre de tri */}
                <div>
                  <Label htmlFor="sortOrder">Ordre</Label>
                  <Select value={sortOrder} onValueChange={setSortOrder}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Décroissant</SelectItem>
                      <SelectItem value="asc">Croissant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Compteurs */}
              <div className="mt-4 flex gap-4 text-sm">
                <div>
                  <span className="font-semibold">{filteredPVList.length}</span> PV affichés
                  {filteredPVList.length !== pvList.length && (
                    <span className="text-gray-500"> sur {pvList.length} total</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="pv-list-container">
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="pv-table-landscape">
              <thead>
                <tr>
                  <th className="w-1/6">Désignation</th>
                  <th className="w-1/8">Type</th>
                  <th className="w-1/10">Date réception</th>
                  <th className="w-1/10">Date limite</th>
                  <th className="w-1/10">Coût</th>
                  <th className="w-1/8">Statut</th>
                  <th className="w-1/8">Pénalité</th>
                  <th className="w-1/6">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPVList.map((pv) => (
                  <tr key={pv.id} className="pv-row hover:bg-gray-50">
                    <td>
                      <div className="font-semibold text-sm">{pv.nom_chantier}</div>
                      <div className="text-xs text-gray-500">
                        {pv.type_intervention === 'direct' ? 'Direct' : 'Sous-traitance'}
                      </div>
                    </td>
                    <td className="text-sm">
                      {pv.type_operation.charAt(0).toUpperCase() + pv.type_operation.slice(1)}
                    </td>
                    <td className="text-sm">
                      {new Date(pv.date_reception).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="text-sm">
                      {new Date(pv.date_limite_depot).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="text-sm font-semibold">
                      {pv.cout_ht_intervention.toFixed(2)}€
                    </td>
                    <td>
                      {getStatusBadge(pv)}
                    </td>
                    <td className="text-sm">
                      {getPenaliteText(pv) || 'En attente'}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        {!pv.date_depot_pv && (
                          <Dialog open={uploadDialogOpen && selectedPv?.id === pv.id} onOpenChange={(open) => {
                            setUploadDialogOpen(open);
                            if (!open) setSelectedPv(null);
                          }}>
                            <DialogTrigger asChild>
                              <Button size="sm" onClick={() => setSelectedPv(pv)}>
                                <Upload className="h-4 w-4 mr-1" />
                                Déposer
                              </Button>
                            </DialogTrigger>
                            <DialogContent key={`upload-dialog-${pv.id}`}>
                              <DialogHeader>
                                <DialogTitle>Déposer le Procès-Verbal</DialogTitle>
                                <DialogDescription>
                                  Téléversez le document PV signé (PDF, JPEG, PNG)
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label htmlFor="pvFile">Document PV *</Label>
                                  <Input
                                    id="pvFile"
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => setUploadFile(e.target.files[0])}
                                  />
                                </div>
                                <Alert>
                                  <AlertCircle className="h-4 w-4" />
                                  <AlertDescription>
                                    Le système calculera automatiquement les pénalités ou remises en fonction de votre date de dépôt.
                                  </AlertDescription>
                                </Alert>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => handleUploadPv(pv.id)}
                                    disabled={!uploadFile || uploadLoading}
                                    className="flex-1"
                                  >
                                    {uploadLoading ? 'Envoi...' : 'Déposer'}
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setUploadDialogOpen(false);
                                      setSelectedPv(null);
                                      setUploadFile(null);
                                    }}
                                    variant="outline"
                                    className="flex-1"
                                  >
                                    Annuler
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                        {pv.date_depot_pv && pv.document_url && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => window.open(`${BACKEND_URL}${pv.document_url}`, '_blank')}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Voir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default ProcesVerbaux;
